/**
 * useServiceBookingFlow Hook
 *
 * Manages the service booking flow for "service" meeting types.
 * Handles service selection, provider selection, and price calculations.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useBookingData } from '../providers/BookingDataProvider';
import productService from '../services/productService';
import {
  PublicProduct,
  PublicProvider,
  ServiceBookingSelection,
  resolveProductPrice,
} from '../types/product';

interface UseServiceBookingFlowOptions {
  /** Username of the meeting type owner */
  username: string;
  /** Meeting type URL slug */
  slug: string;
  /** Whether the meeting type is a service type */
  isServiceType: boolean;
  /** Initial service ID (for returning from calendar - skips initial loading state) */
  initialServiceId?: number;
  /** Meeting type ID - used for preview mode when username/slug are not available */
  meetingTypeId?: number;
}

interface UseServiceBookingFlowReturn {
  // Data
  serviceOptions: PublicProduct[];
  providers: PublicProvider[];
  selectedService: PublicProduct | null;
  selectedProvider: PublicProvider | null;
  /** Raw provider ID: undefined = not yet selected, null = No Preference, number = specific */
  selectedProviderId: number | null | undefined;
  selection: ServiceBookingSelection | null;

  // Loading states
  loadingServices: boolean;
  loadingProviders: boolean;

  // Errors
  servicesError: string | null;
  providersError: string | null;

  // Actions
  selectService: (serviceId: number) => void;
  selectProvider: (providerId: number | null) => void;
  selectDuration: (duration: number) => void;
  clearSelection: () => void;

  // Computed values
  effectiveDuration: number;
  effectivePrice: number;
  isNoPreference: boolean;
  hasProviderSelection: boolean;
  /** The host turned the person-in-charge step off, and there is one host. */
  skipProviderStep: boolean;
  /** True when services have been loaded and there are no service options */
  hasNoServiceOptions: boolean;
  /** True when services have finished loading */
  servicesLoaded: boolean;
  /** Available durations from meeting type (for duration mode) */
  availableDurations: number[];
  /** Selected duration (for duration mode) */
  selectedDuration: number | null;

  // Availability refresh
  refreshAvailability: () => Promise<void>;
}

/**
 * Hook for managing service booking flow
 */
export const useServiceBookingFlow = ({
  username,
  slug,
  isServiceType,
  initialServiceId,
  meetingTypeId,
}: UseServiceBookingFlowOptions): UseServiceBookingFlowReturn => {
  // Get booking data context for availability prefetching
  const {
    meetingType,
    prefetchMonthSlots,
    clearCachedData,
    setSelectedProviderId: setBookingProviderFilter,
  } = useBookingData();

  // Service options state
  const [serviceOptions, setServiceOptions] = useState<PublicProduct[]>([]);
  const [loadingServices, setLoadingServices] = useState<boolean>(false);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [servicesLoaded, setServicesLoaded] = useState<boolean>(false);

  // Provider state
  const [providers, setProviders] = useState<PublicProvider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const [providersError, setProvidersError] = useState<string | null>(null);

  // Selection state
  // selectedProviderId: undefined = not yet selected, null = "No Preference", number = specific provider
  // Initialize with initialServiceId if provided (for returning from calendar)
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(initialServiceId || null);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null | undefined>(undefined);
  // Duration selection (for duration mode - when serviceMode === 'duration')
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);

  // Track if this is a "returning" mount (has initialServiceId) to skip loading state flicker
  const isReturningMount = Boolean(initialServiceId);

  // Track if we've done the initial provider fetch (for returning mounts)
  const hasInitialProviderFetchRef = useRef(false);

  // Fetch service options on mount (only for service types)
  useEffect(() => {
    if (!isServiceType) {
      return;
    }

    // Support both public (username/slug) and preview (meetingTypeId) modes
    const canFetchPublic = username && slug;
    const canFetchPreview = meetingTypeId && meetingTypeId > 0;

    if (!canFetchPublic && !canFetchPreview) {
      return;
    }

    const fetchServiceOptions = async () => {
      setLoadingServices(true);
      setServicesError(null);

      try {
        let options: PublicProduct[];

        if (canFetchPublic) {
          // Normal booking flow: use public endpoint with username/slug
          options = await productService.getPublicProducts(username, slug);
        } else {
          // Preview mode: use authenticated endpoint with meeting type ID
          options = await productService.getProductsForPreview(meetingTypeId!);
        }

        setServiceOptions(options);
        setServicesLoaded(true);
      } catch (error: any) {
        setServicesError(
          error.response?.data?.message || error.message || 'Failed to load services'
        );
        setServicesLoaded(true); // Also set loaded on error so we know fetch completed
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServiceOptions();
  }, [isServiceType, username, slug, meetingTypeId]);

  // Fetch providers when service is selected
  useEffect(() => {
    if (!isServiceType) {
      return;
    }

    // Support both public (username/slug) and preview (meetingTypeId) modes
    const canFetchPublic = username && slug;
    const canFetchPreview = meetingTypeId && meetingTypeId > 0;

    if (!canFetchPublic && !canFetchPreview) {
      return;
    }

    const fetchProviders = async () => {
      // Skip loading state on initial fetch when returning from calendar
      // This prevents the "blink" caused by showing/hiding the loading spinner
      const skipLoadingState = isReturningMount && !hasInitialProviderFetchRef.current;
      hasInitialProviderFetchRef.current = true;

      if (!skipLoadingState) {
        setLoadingProviders(true);
      }
      setProvidersError(null);

      try {
        let providerList: PublicProvider[];

        if (canFetchPublic) {
          // Normal booking flow: use public endpoint with username/slug
          providerList = await productService.getPublicProviders(
            username,
            slug,
            selectedServiceId || undefined
          );
        } else {
          // Preview mode: use authenticated endpoint with meeting type ID
          providerList = await productService.getProvidersForPreview(
            meetingTypeId!,
            selectedServiceId || undefined
          );
        }

        setProviders(providerList);
      } catch (error: any) {
        setProvidersError(
          error.response?.data?.message || error.message || 'Failed to load providers'
        );
      } finally {
        setLoadingProviders(false);
      }
    };

    fetchProviders();
  }, [isServiceType, username, slug, meetingTypeId, selectedServiceId, isReturningMount]);

  // Get currently selected service object
  const selectedService = useMemo((): PublicProduct | null => {
    if (!selectedServiceId) return null;
    return serviceOptions.find((s) => s.id === selectedServiceId) || null;
  }, [serviceOptions, selectedServiceId]);

  // Get currently selected provider object
  const selectedProvider = useMemo((): PublicProvider | null => {
    if (selectedProviderId === null) return null; // No Preference selected
    if (selectedProviderId === undefined) return null; // Not yet selected
    // Use Number() to handle potential string/number type mismatch
    return providers.find((p) => Number(p.userId) === Number(selectedProviderId)) || null;
  }, [providers, selectedProviderId]);

  // Get available durations from meeting type
  const availableDurations = useMemo((): number[] => {
    return meetingType?.duration || [30];
  }, [meetingType?.duration]);

  // Calculate effective duration (from selected service OR selected duration for duration mode)
  const effectiveDuration = useMemo((): number => {
    if (selectedService) {
      // durationMinutes is nullable on a Product — only service items carry one.
      return selectedService.durationMinutes ?? meetingType?.duration?.[0] ?? 30;
    }
    // Duration mode: use selected duration or first available
    if (selectedDuration !== null) {
      return selectedDuration;
    }
    // Fallback to meeting type's default duration
    return meetingType?.duration?.[0] || 30;
  }, [selectedService, selectedDuration, meetingType]);

  // Calculate effective price (service price + provider fee)
  const effectivePrice = useMemo((): number => {
    const servicePrice = selectedService ? resolveProductPrice(selectedService) : 0;
    const providerFee = Number(selectedProvider?.additionalFee) || 0;
    return servicePrice + providerFee;
  }, [selectedService, selectedProvider]);

  // Check if "No Preference" is selected (null = No Preference, undefined = not yet selected)
  const isNoPreference = useMemo((): boolean => {
    return selectedProviderId === null;
  }, [selectedProviderId]);

  // Check if there are no service options (services loaded but empty, OR serviceMode is 'duration')
  // When serviceMode is 'duration', we skip the service selection step entirely
  /**
   * This page does not use a service menu AT ALL — it books by duration.
   *
   * ⚠️ NOT "there are no options right now". Those are different pages and
   * conflating them is what let a page with everything withdrawn walk the
   * guest straight past an empty menu to a calendar and book for free: a host
   * who takes every service off sale, or whose gating plan is switched off,
   * has stopped selling — not switched to duration booking. `serviceMode` is
   * the only thing that knows which page this is, and the server refuses on
   * the same rule.
   *
   * An options page with nothing on it now simply stays on the service step,
   * where `ServiceSelection` already says there is nothing available.
   */
  const hasNoServiceOptions = useMemo((): boolean => {
    return meetingType?.serviceMode === 'duration';
  }, [meetingType?.serviceMode]);

  /**
   * The host has turned the person-in-charge step off.
   *
   * Only honoured with ONE host: with two, skipping would decide for the
   * invitee rather than save them a click. The editor only offers the switch in
   * that case, and this re-checks rather than trusting the flag, because a
   * second host added later must bring the step back.
   */
  const skipProviderStep = useMemo((): boolean => {
    const settings = (meetingType as any)?.hostDisplaySettings;
    return settings?.skipProviderSelection === true && (meetingType?.hosts?.length ?? 0) <= 1;
  }, [meetingType]);

  // Check if provider has been selected (either No Preference or specific provider)
  // When there are no service options, only provider selection is needed
  const hasProviderSelection = useMemo((): boolean => {
    // Nothing to select: the single host is implied.
    if (skipProviderStep) {
      return hasNoServiceOptions ? true : selectedServiceId !== null;
    }
    if (hasNoServiceOptions) {
      // No service options - only need provider selection
      return selectedProviderId !== undefined;
    }
    // Normal case - need both service and provider selection
    return selectedProviderId !== undefined && selectedServiceId !== null;
  }, [selectedServiceId, selectedProviderId, hasNoServiceOptions, skipProviderStep]);

  // Build the complete selection object
  const selection = useMemo((): ServiceBookingSelection | null => {
    if (!selectedService) return null;

    return {
      productId: selectedService.id,
      selectedProviderId: selectedProviderId,
      serviceName: selectedService.title,
      providerName: selectedProvider?.displayName || null,
      durationMinutes: selectedService.durationMinutes ?? 30,
      servicePrice: resolveProductPrice(selectedService),
      providerFee: selectedProvider?.additionalFee || 0,
      totalPrice: effectivePrice,
    };
  }, [selectedService, selectedProviderId, selectedProvider, effectivePrice]);

  // Select a service
  const selectService = useCallback((serviceId: number) => {
    setSelectedServiceId(serviceId);
    // Reset provider selection when service changes (undefined = not yet selected)
    setSelectedProviderId(undefined);
  }, []);

  // Select a provider (null = No Preference)
  // Also bridges to BookingDataProvider so availability API includes providerId
  const selectProvider = useCallback((providerId: number | null) => {
    setSelectedProviderId(providerId);
    // Bridge to BookingDataProvider — triggers cache clear + refetch with new provider filter
    setBookingProviderFilter(providerId);
  }, [setBookingProviderFilter]);

  // Select a duration (for duration mode)
  const selectDuration = useCallback((duration: number) => {
    setSelectedDuration(duration);
  }, []);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedServiceId(null);
    setSelectedProviderId(undefined);
    setSelectedDuration(null);
  }, []);

  // Refresh availability when provider or service changes
  const refreshAvailability = useCallback(async () => {
    if (!meetingType || !prefetchMonthSlots) {
      return;
    }

    // Clear cached data and refetch with new duration
    clearCachedData();

    // Prefetch current and next month with the selected service's duration
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    await Promise.all([
      prefetchMonthSlots(currentMonth, timezone, effectiveDuration),
      prefetchMonthSlots(nextMonth, timezone, effectiveDuration),
    ]);
  }, [meetingType, prefetchMonthSlots, clearCachedData, effectiveDuration]);

  // Auto-refresh availability when selection changes
  useEffect(() => {
    if (selectedService && isServiceType) {
      refreshAvailability();
    }
  }, [selectedService, isServiceType]); // Don't include refreshAvailability to avoid infinite loop

  return {
    // Data
    serviceOptions,
    providers,
    selectedService,
    selectedProvider,
    selectedProviderId,
    selection,

    // Loading states
    loadingServices,
    loadingProviders,

    // Errors
    servicesError,
    providersError,

    // Actions
    selectService,
    selectProvider,
    selectDuration,
    clearSelection,

    // Computed values
    effectiveDuration,
    effectivePrice,
    isNoPreference,
    hasProviderSelection,
    skipProviderStep,
    hasNoServiceOptions,
    servicesLoaded,
    availableDurations,
    selectedDuration,

    // Availability refresh
    refreshAvailability,
  };
};

export default useServiceBookingFlow;
